import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { API } from "@/lib/api";

// Les CV sont servis depuis le dossier Drive « Fiches » (modifiables par l'utilisatrice)
const fiche = (nom) => `${API}/public/fiches/${encodeURIComponent(nom)}`;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const PEOPLE = [
  {
    name: "Jean-Sébastien Mével",
    img: "https://images.squarespace-cdn.com/content/v1/65ea2001529ccd1a677ed52f/e00c9515-55da-425c-9ccb-df9fbac4a046/Capture+d%E2%80%99e%CC%81cran+2024-04-25+a%CC%80+14.57.44.png",
    cv: fiche("CV Jean-Sébastien Mével.pdf"),
    bio: [
      <>
        Après une formation dans les domaines du commerce et de la finance, il décide, en 1994, de créer sa
        compagnie de théâtre avec laquelle il créera une dizaine de spectacles dont{" "}
        <em className="text-[#F3F4F6]">L'indien cherche le Bronx</em> (Horovitz, mise en scène M.O. Cayre, 1995),{" "}
        <em className="text-[#F3F4F6]">Le Chandelier</em> (Musset, mise en scène M.O. Cayre, 1996),{" "}
        <em className="text-[#F3F4F6]">Savannah Bay</em> (Marguerite Duras, mise en scène C. Sermet, 1998),{" "}
        <em className="text-[#F3F4F6]">Les Misérables</em> (Victor Hugo, mise en scène Denis Llorca, 1999-00),{" "}
        <em className="text-[#F3F4F6]">Falstaff</em> (d'après Shakespeare, mise en scène Claude Brosset, 2000),{" "}
        <em className="text-[#F3F4F6]">Premier Combat</em> (Jean Moulin, mise en scène Jean-Paul Zehnacker, 2001),{" "}
        <em className="text-[#F3F4F6]">Bonne Nouvelle sur les Boulevard</em> (Festival musical, 2003),{" "}
        <em className="text-[#F3F4F6]">Lulu ou la nuit d'octobre</em> (co-auteur, 2013)…
      </>,
      "Auteur (théâtre, court métrage) et, à ses heures, réalisateur, il est également administrateur depuis 2008 des Cours et de la Compagnie Acquaviva, ainsi que de l'ensemble de musique contemporaine Soundinitiative. Il a également travaillé dans les domaines du cinéma et de l'audiovisuel en tant qu'administrateur et assistant de production, notamment chez Agat Film & Ex-Nihilo.",
      "Il intervient auprès d'une dizaine de compagnies dans l'accompagnement de leurs projets, tant à Paris que sur Avignon où il est présent depuis 1996.",
      "En 2017, il crée sa structure de gestion des payes « Le 30ème du mois ». Plus de 80 compagnies y sont affiliées à ce jour et les payes de plus de 700 spectacles ont été réalisées.",
    ],
  },
  {
    name: "Marilyn Thuault",
    img: "https://images.squarespace-cdn.com/content/v1/65ea2001529ccd1a677ed52f/a61011e4-04f3-40e9-b6a2-1a542d567678/Marilyn.png",
    cv: fiche("CV Marilyn Thuault.pdf"),
    zoom: true,
    bio: [
      "Marilyn débute la danse dès l'âge de 7 ans. Tout d'abord classique, elle s'oriente ensuite vers les styles contemporains, le hip hop et le modern'jazz jusqu'à décrocher son diplôme d'état de professeur de danse. En 2007, elle crée son association de cours de danse « Créa'Danse » tout en poursuivant une activité professionnelle d'artiste chorégraphique.",
      "Parallèlement à ce parcours professionnel artistique, riche de plus de 20 ans d'expérience, elle obtient une licence en sociologie et un diplôme de responsable d'administration d'entreprises culturelles.",
      "Depuis 2019, elle se consacre aux questions administratives, organisationnelles et logistiques de compagnies de danse et de théâtre : gestion administrative, budgétaire, financière, juridique et sociale…",
      "Grâce à son parcours, tout à la fois artistique et administratif, elle est aujourd'hui, à travers la paie, accompagnatrice de tous vos projets artistiques.",
    ],
  },
];

export function Producteur() {
  // Portraits « vivants » : 2,5 s d'animation puis 10 s de pause, à tour de rôle
  const [alive, setAlive] = useState(-1);
  useEffect(() => {
    let n = 0;
    let id;
    const start = setTimeout(() => {
      setAlive(0);
      id = setInterval(() => { n += 1; setAlive(n % PEOPLE.length); }, 12500);
    }, 4000);
    return () => { clearTimeout(start); if (id) clearInterval(id); };
  }, []);

  return (
    <section data-testid="producteur-section" className="py-24 md:py-32 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div {...fadeUp} className="max-w-2xl mb-20">
          <p className="font-mono text-sm md:text-base tracking-[0.3em] uppercase text-gold mb-6">/ Et… accessoirement !</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] mb-8">
            Le 30 du mois est producteur de <span className="italic text-gold">spectacle vivant</span>
          </h2>
          <p className="text-[#A0B2CE] text-lg leading-relaxed">
            Le 30ème du Mois est également producteur de spectacle vivant et, à titre individuel, Jean-Sébastien
            Mével et Marilyn Thuault ont un riche parcours dans les domaines du spectacle vivant… Théâtre, danse,
            musique et formation professionnelle.
          </p>
        </motion.div>

        <div className="space-y-24">
          {PEOPLE.map((p, i) => (
            <motion.article
              key={p.name}
              {...fadeUp}
              data-testid={`producteur-${i}`}
              className={`grid gap-10 md:gap-16 items-start ${i % 2 ? "md:grid-cols-[1fr_280px] md:[&>figure]:order-2" : "md:grid-cols-[280px_1fr]"}`}
            >
              <figure className={`portrait-card w-full max-w-[280px] rounded-2xl border border-white/10 overflow-hidden${alive === i ? " portrait-vit" : ""}`} data-testid={`portrait-${i}`}>
                <img
                  src={p.img}
                  alt={`Portrait de ${p.name}`}
                  loading="lazy"
                  className={`w-full aspect-[4/5] object-cover ${p.zoom ? "scale-[1.05]" : ""}`}
                />
              </figure>
              <div>
                <h3 className="font-serif text-3xl mb-6">{p.name}</h3>
                <div className="space-y-4 text-[#A0B2CE] leading-relaxed">
                  {p.bio.map((t, j) => (
                    <p key={j}>{t}</p>
                  ))}
                </div>
                <a
                  href={p.cv}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`cv-${i}`}
                  className="group inline-flex items-center gap-2 mt-8 font-mono text-xs uppercase tracking-[0.15em] text-gold hover:text-white transition-colors"
                >
                  Télécharger le CV
                  <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
